import type { ReactNode } from 'react'
import React, { createContext, useMemo, useState } from 'react'

type Props = { children: ReactNode }

export type PreviewMode = 'desktop' | 'mobile'

export type ThemeEditorContextType = {
  previewMode: PreviewMode
  previewTimestamp: number
  isPreviewLoading: boolean
  currentPageId: string
  currentSectionKey: string | undefined
  currentSectionId: string | undefined
  setPreviewMode: (previewMode: PreviewMode) => void
  setPreviewTimestamp: (previewTimestamp: number) => void
  resetPreviewTimestamp: () => void
  setIsPreviewLoading: (isPreviewLoading: boolean) => void
  setCurrentPageId: (currentPageId: string) => void
  setCurrentSectionKey: (currentSectionKey: string | undefined) => void
  setCurrentSectionId: (currentSectionId: string | undefined) => void
}

export const ThemeEditorContext = createContext<ThemeEditorContextType | undefined>(undefined)

export const ThemeEditorProvider = ({ children }: Props) => {
  const [previewMode, setPreviewMode] = useState<PreviewMode>('desktop')
  const [previewTimestamp, setPreviewTimestamp] = useState(new Date().valueOf())
  const [isPreviewLoading, setIsPreviewLoading] = useState(true)
  const [currentPageId, setCurrentPageId] = useState('index')
  const [currentSectionKey, setCurrentSectionKey] = useState<string | undefined>()
  const [currentSectionId, setCurrentSectionId] = useState<string | undefined>()

  const resetPreviewTimestamp = () => {
    setPreviewTimestamp(new Date().valueOf())
    setIsPreviewLoading(false)
  }

  const handleSetCurrentPageId = (pageId: string) => {
    setCurrentPageId(pageId)
  }

  const value = useMemo(
    () => ({
      previewMode,
      previewTimestamp,
      isPreviewLoading,
      currentPageId,
      currentSectionKey,
      currentSectionId,
      setPreviewMode,
      setPreviewTimestamp,
      resetPreviewTimestamp,
      setIsPreviewLoading,
      setCurrentPageId: handleSetCurrentPageId,
      setCurrentSectionKey,
      setCurrentSectionId,
    }),
    [
      currentPageId,
      currentSectionId,
      currentSectionKey,
      isPreviewLoading,
      previewMode,
      previewTimestamp,
    ],
  )

  return <ThemeEditorContext.Provider value={value}>{children}</ThemeEditorContext.Provider>
}
